import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import { Col, Row } from "antd/es/grid";
import { defineMessages, useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import StickyBar from "@app/scene/map/components/tools/StickyBar";
import { selectUser } from "@app/scene/map/store/actions";
import { getSelectedUser } from "@app/scene/map/store/selectors";
import isUserLocationLayerAvailable from "@app/scene/map/utils/isUserLocationLayerAvailable";
import { getMe, getOrganizationSettings } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { userDisplayName } from "@app/util/formatters";

import styles from "./UserLocationPanel.module.scss";

const messages = defineMessages({
  exit: {
    id: "map.tool.userLocation.toolbar.exit",
    defaultMessage: "Exit Location History",
    description: "User location panel - Exit Location History",
  },
  title: {
    id: "map.tool.userLocation.toolbar.title",
    defaultMessage: "You are viewing {username}’s Location History",
    description: "User location panel - title",
  },
});

interface Props {
  isUserLayerAvailable: boolean;
  selectedUser?: User;
  selectUser: typeof selectUser;
}

const UserLocationPanel: React.FC<Props> = ({ isUserLayerAvailable, selectedUser, selectUser }) => {
  const intl = useIntl();

  const handleExit = useCallback(() => {
    selectUser(undefined);
  }, [selectUser]);

  useEffect(() => {
    if (!isUserLayerAvailable) {
      selectUser(undefined);
    }
  }, [isUserLayerAvailable, selectUser]);

  if (!selectedUser || !isUserLayerAvailable) {
    return null;
  }

  return (
    <Interactive>
      <StickyBar className={styles.container}>
        <Row align="middle" justify="space-between">
          <Col span={16}>
            {intl.formatMessage(messages.title, { username: userDisplayName(selectedUser) })}
          </Col>
          <Col span={8}>
            <Row justify="end">
              <Col>
                <Button onClick={handleExit}>{intl.formatMessage(messages.exit)}</Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </StickyBar>
    </Interactive>
  );
};

const mapStateToProps = (state: RootState) => ({
  isUserLayerAvailable: isUserLocationLayerAvailable(getMe(state)!, getOrganizationSettings(state)),
  selectedUser: getSelectedUser(state),
});

const mapDispatchToProps = {
  selectUser,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserLocationPanel);
